import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from './config';
import { uploadFile, deleteFile } from './r2storage';
import { COLLECTIONS } from './collections';

// Admissions Service
export const admissionsService = {
  // Get all applications (admin only - no filtering)
  async getAllApplications() {
    try {
      const q = query(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS));
      const querySnapshot = await getDocs(q);
      const applications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        applications.push({ 
          id: docSnap.id, 
          ...data,
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      }
      
      // Sort by createdAt
      applications.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      
      return { success: true, applications };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get all institutions (schools, colleges, coachings)
  async getAllInstitutions(filters = {}) {
    try {
      let q = query(collection(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS));
      
      // Apply filters
      if (filters.isActive !== undefined) {
        q = query(q, where('isActive', '==', filters.isActive));
      }
      
      if (filters.type) {
        q = query(q, where('type', '==', filters.type));
      }
      
      if (filters.city) {
        q = query(q, where('address.city', '==', filters.city));
      }
      
      // Try to order by createdAt, but handle if index doesn't exist
      try {
        q = query(q, orderBy('createdAt', 'desc'));
      } catch (orderError) {
        // If ordering fails (index not created), continue without ordering
      }
      
      const querySnapshot = await getDocs(q);
      const institutions = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        institutions.push({ 
          id: doc.id, 
          ...data,
          // Ensure createdAt exists for sorting
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      });
      
      // Sort manually if orderBy failed
      if (filters.isActive !== undefined || filters.type) {
        institutions.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
      }
      
      return { success: true, institutions };
    } catch (error) {
      // Fallback: try without filters if query fails
      try {
        const q = query(collection(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS));
        const querySnapshot = await getDocs(q);
        const institutions = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Apply filters manually
          if (filters.isActive !== undefined && data.isActive !== filters.isActive) {
            return;
          }
          if (filters.type && data.type !== filters.type) {
            return;
          }
          if (filters.city && data.address?.city !== filters.city) {
            return;
          }
          
          institutions.push({ 
            id: doc.id, 
            ...data,
            createdAt: data.createdAt || data.updatedAt || new Date()
          });
        });
        
        // Sort by createdAt
        institutions.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        
        return { success: true, institutions };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Get institution by ID
  async getInstitutionById(institutionId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS, institutionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, institution: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: false, error: 'Institution not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Create or update institution
  async saveInstitution(institutionData, institutionId = null) {
    try {
      const data = {
        ...institutionData,
        updatedAt: serverTimestamp()
      };

      if (institutionId) {
        // Check if document exists
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS, institutionId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          // Update existing - preserve createdAt if it exists
          const existingData = docSnap.data();
          if (existingData.createdAt) {
            // Don't overwrite createdAt on update
            delete data.createdAt;
          }
          await updateDoc(docRef, data);
          return { success: true, institutionId };
        } else {
          // Document doesn't exist, create it with the specified ID
          data.createdAt = serverTimestamp();
          data.isActive = data.isActive !== undefined ? data.isActive : true;
          // Ensure isActive is explicitly set
          if (data.isActive === undefined) {
            data.isActive = true;
          }
          await setDoc(docRef, data);
          return { success: true, institutionId };
        }
      } else {
        // Create new with auto-generated ID
        data.createdAt = serverTimestamp();
        data.isActive = data.isActive !== undefined ? data.isActive : true;
        // Ensure isActive is explicitly set
        if (data.isActive === undefined) {
          data.isActive = true;
        }
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS), data);
        return { success: true, institutionId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload institution logo/image
  async uploadInstitutionImage(file, institutionId) {
    try {
      const storagePath = `admissions/institutions/${institutionId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      return { success: true, url: result.url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Submit application
  async submitApplication(applicationData) {
    try {
      const data = {
        ...applicationData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS), data);
      return { success: true, applicationId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get payment amount for an application
  async getApplicationPaymentAmount(applicationId, transactionId) {
    try {
      // First try to get from admissions payments collection
      if (transactionId) {
        const paymentQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS),
          where('transactionId', '==', transactionId)
        );
        const paymentSnapshot = await getDocs(paymentQuery);
        if (!paymentSnapshot.empty) {
          const paymentData = paymentSnapshot.docs[0].data();
          if (paymentData.amount) {
            return paymentData.amount;
          }
        }
      }

      // Try to get from payment transactions collection
      if (transactionId) {
        const { paymentManagementService } = await import('./paymentManagement');
        // This would require a function to get transaction by ID
        // For now, return null and let the caller use fallback
      }

      return null;
    } catch (error) {
      return null;
    }
  },

  // Get applications for an institution
  async getInstitutionApplications(institutionId) {
    // Get institution to get default application fee
    const institutionResult = await this.getInstitutionById(institutionId);
    const defaultFee = institutionResult.success ? (institutionResult.institution.applicationFee || 0) : 0;
    
    // Try query with orderBy first
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS),
        where('institutionId', '==', institutionId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const applications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // If paymentAmount is missing, try to get it from payment record
        let paymentAmount = data.paymentAmount;
        if (!paymentAmount && data.paymentTransactionId) {
          const fetchedAmount = await this.getApplicationPaymentAmount(docSnap.id, data.paymentTransactionId);
          if (fetchedAmount) {
            paymentAmount = fetchedAmount;
          }
        }
        
        applications.push({ 
          id: docSnap.id, 
          ...data,
          paymentAmount: paymentAmount || defaultFee,
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      }
      
      return { success: true, applications };
    } catch (error) {
      // If error is about missing index, try without orderBy
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        try {
          const q = query(
            collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS),
            where('institutionId', '==', institutionId)
          );
          
          const querySnapshot = await getDocs(q);
          const applications = [];
          
          for (const docSnap of querySnapshot.docs) {
            const data = docSnap.data();
            
            // If paymentAmount is missing, try to get it from payment record
            let paymentAmount = data.paymentAmount;
            if (!paymentAmount && data.paymentTransactionId) {
              const fetchedAmount = await this.getApplicationPaymentAmount(docSnap.id, data.paymentTransactionId);
              if (fetchedAmount) {
                paymentAmount = fetchedAmount;
              }
            }
            
            applications.push({ 
              id: docSnap.id, 
              ...data,
              paymentAmount: paymentAmount || defaultFee,
              createdAt: data.createdAt || data.updatedAt || new Date()
            });
          }
          
          // Sort manually
          applications.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
          
          return { success: true, applications };
        } catch (fallbackError) {
          // Final fallback: fetch all and filter manually
          return this.getInstitutionApplicationsFallback(institutionId, defaultFee);
        }
      } else {
        // Final fallback: fetch all and filter manually
        return this.getInstitutionApplicationsFallback(institutionId, defaultFee);
      }
    }
  },

  // Fallback method to fetch all applications and filter manually
  async getInstitutionApplicationsFallback(institutionId, defaultFee) {
    try {
      const q = query(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS));
      const querySnapshot = await getDocs(q);
      const applications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        // Apply filter manually
        if (data.institutionId !== institutionId) {
          continue;
        }
        
        // If paymentAmount is missing, try to get it from payment record
        let paymentAmount = data.paymentAmount;
        if (!paymentAmount && data.paymentTransactionId) {
          const fetchedAmount = await this.getApplicationPaymentAmount(docSnap.id, data.paymentTransactionId);
          if (fetchedAmount) {
            paymentAmount = fetchedAmount;
          }
        }
        
        applications.push({ 
          id: docSnap.id, 
          ...data,
          paymentAmount: paymentAmount || defaultFee,
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      }
      
      // Sort by createdAt
      applications.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      
      return { success: true, applications };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get user applications
  async getUserApplications(userId) {
    try {
      let q = query(
        collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS),
        where('userId', '==', userId)
      );
      
      // Try to order by createdAt, but handle if index doesn't exist
      try {
        q = query(q, orderBy('createdAt', 'desc'));
      } catch (orderError) {
        // If ordering fails (index not created), continue without ordering
      }
      
      const querySnapshot = await getDocs(q);
      const applications = [];
      
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        
        // If paymentAmount is missing, try to get it from payment record
        let paymentAmount = data.paymentAmount;
        if (!paymentAmount && data.paymentTransactionId) {
          const fetchedAmount = await this.getApplicationPaymentAmount(docSnap.id, data.paymentTransactionId);
          if (fetchedAmount) {
            paymentAmount = fetchedAmount;
          }
        }
        
        // If still no amount, try to get from institution
        if (!paymentAmount && data.institutionId) {
          const institutionResult = await this.getInstitutionById(data.institutionId);
          if (institutionResult.success) {
            paymentAmount = institutionResult.institution.applicationFee || 0;
          }
        }
        
        applications.push({ 
          id: docSnap.id, 
          ...data,
          paymentAmount: paymentAmount || 0,
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      }
      
      // Sort manually if orderBy failed
      applications.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      
      return { success: true, applications };
    } catch (error) {
      // Fallback: try without filters if query fails
      try {
        const q = query(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS));
        const querySnapshot = await getDocs(q);
        const applications = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Apply filter manually
          if (data.userId !== userId) {
            return;
          }
          
          applications.push({ 
            id: doc.id, 
            ...data,
            paymentAmount: data.paymentAmount || 0,
            createdAt: data.createdAt || data.updatedAt || new Date()
          });
        });
        
        // Sort by createdAt
        applications.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        
        return { success: true, applications };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Update application status
  async updateApplicationStatus(applicationId, status, notes = '') {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_APPLICATIONS, applicationId);
      await updateDoc(docRef, {
        status,
        notes,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save application payment
  async saveApplicationPayment(paymentData) {
    try {
      const data = {
        ...paymentData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS), data);
      return { success: true, paymentId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get institution customization
  async getInstitutionCustomization(institutionId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_CUSTOMIZATIONS, institutionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, customization: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: true, customization: null };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save institution customization
  async saveInstitutionCustomization(institutionId, customizationData) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_CUSTOMIZATIONS, institutionId);
      await setDoc(docRef, {
        ...customizationData,
        institutionId,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload customization image
  async uploadCustomizationImage(file, institutionId, imageType) {
    try {
      const storagePath = `admissions/customizations/${institutionId}/${imageType}_${Date.now()}_${file.name}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      // Store fileKey instead of URL for proper R2 presigned URL regeneration
      return { success: true, url: result.fileKey || result.path || result.url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== CURRICULUM MANAGEMENT ==========
  // Get curriculum for institution
  async getInstitutionCurriculum(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM),
        where('institutionId', '==', institutionId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const curriculum = [];
      querySnapshot.forEach((doc) => {
        curriculum.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, curriculum };
    } catch (error) {
      // Fallback if index doesn't exist
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const curriculum = [];
        querySnapshot.forEach((doc) => {
          curriculum.push({ id: doc.id, ...doc.data() });
        });
        curriculum.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        return { success: true, curriculum };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Save curriculum item
  async saveCurriculumItem(curriculumData, curriculumId = null) {
    try {
      const data = {
        ...curriculumData,
        updatedAt: serverTimestamp()
      };

      if (curriculumId) {
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_CURRICULUM, curriculumId);
        await updateDoc(docRef, data);
        return { success: true, curriculumId };
      } else {
        data.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM), data);
        return { success: true, curriculumId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete curriculum item
  async deleteCurriculumItem(curriculumId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ADMISSIONS_CURRICULUM, curriculumId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== MATERIALS MANAGEMENT ==========
  // Get materials for institution
  async getInstitutionMaterials(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_MATERIALS),
        where('institutionId', '==', institutionId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const materials = [];
      querySnapshot.forEach((doc) => {
        materials.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, materials };
    } catch (error) {
      // Fallback if index doesn't exist
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_MATERIALS),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const materials = [];
        querySnapshot.forEach((doc) => {
          materials.push({ id: doc.id, ...doc.data() });
        });
        materials.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        return { success: true, materials };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Save material
  async saveMaterial(materialData, materialId = null) {
    try {
      const data = {
        ...materialData,
        updatedAt: serverTimestamp()
      };

      if (materialId) {
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_MATERIALS, materialId);
        await updateDoc(docRef, data);
        return { success: true, materialId };
      } else {
        data.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_MATERIALS), data);
        return { success: true, materialId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload material file
  async uploadMaterialFile(file, institutionId, materialType) {
    try {
      const storagePath = `admissions/materials/${institutionId}/${materialType}_${Date.now()}_${file.name}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      // Store fileKey instead of URL for proper R2 presigned URL regeneration
      return { success: true, url: result.fileKey || result.path || result.url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete material
  async deleteMaterial(materialId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ADMISSIONS_MATERIALS, materialId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== FACULTY MANAGEMENT ==========
  // Get faculty for institution
  async getInstitutionFaculty(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_FACULTY),
        where('institutionId', '==', institutionId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const faculty = [];
      querySnapshot.forEach((doc) => {
        faculty.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, faculty };
    } catch (error) {
      // Fallback if index doesn't exist
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_FACULTY),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const faculty = [];
        querySnapshot.forEach((doc) => {
          faculty.push({ id: doc.id, ...doc.data() });
        });
        faculty.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        return { success: true, faculty };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Save faculty member
  async saveFacultyMember(facultyData, facultyId = null) {
    try {
      const data = {
        ...facultyData,
        updatedAt: serverTimestamp()
      };

      if (facultyId) {
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_FACULTY, facultyId);
        await updateDoc(docRef, data);
        return { success: true, facultyId };
      } else {
        data.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_FACULTY), data);
        return { success: true, facultyId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload faculty photo
  async uploadFacultyPhoto(file, institutionId, facultyId) {
    try {
      const storagePath = `admissions/faculty/${institutionId}/${facultyId}_${Date.now()}_${file.name}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      // Store fileKey instead of URL for proper R2 presigned URL regeneration
      return { success: true, url: result.fileKey || result.path || result.url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete faculty member
  async deleteFacultyMember(facultyId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ADMISSIONS_FACULTY, facultyId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save refund request
  async saveRefundRequest(refundData) {
    try {
      const data = {
        ...refundData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, COLLECTIONS.PAYMENT_REFUNDS), data);
      return { success: true, refundId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get all refund requests (admin only)
  async getAllRefunds() {
    try {
      const q = query(collection(db, COLLECTIONS.PAYMENT_REFUNDS), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const refunds = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        refunds.push({ 
          id: doc.id, 
          ...data,
          createdAt: data.createdAt || data.updatedAt || new Date()
        });
      });
      
      return { success: true, refunds };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update refund status
  async updateRefundStatus(refundId, status) {
    try {
      const docRef = doc(db, COLLECTIONS.PAYMENT_REFUNDS, refundId);
      const refundDoc = await getDoc(docRef);
      
      if (!refundDoc.exists()) {
        return { success: false, error: 'Refund not found' };
      }

      const refundData = refundDoc.data();
      
      // Update refund status
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp()
      });

      // If refund is marked as refunded, also update the application status
      if (status === 'refunded' && refundData.applicationId) {
        const applicationRef = doc(db, COLLECTIONS.ADMISSIONS_APPLICATIONS, refundData.applicationId);
        await updateDoc(applicationRef, {
          status: 'refunded',
          updatedAt: serverTimestamp()
        });
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Toggle institution visibility (hide/show)
  async toggleInstitutionVisibility(institutionId, isActive) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS, institutionId);
      await updateDoc(docRef, {
        isActive: isActive,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete institution and all related admission data
  async deleteInstitution(institutionId) {
    try {
      // 1. Delete all admission applications for this institution
      try {
        const applicationsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS),
          where('institutionId', '==', institutionId)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        for (const appDoc of applicationsSnapshot.docs) {
          await deleteDoc(appDoc.ref);
        }
      } catch (error) {
        // Try fallback: get all and filter
        try {
          const allAppsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_APPLICATIONS));
          for (const appDoc of allAppsSnapshot.docs) {
            const appData = appDoc.data();
            if (appData.institutionId === institutionId) {
              await deleteDoc(appDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 2. Delete all admission payments for this institution
      try {
        const paymentsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS),
          where('institutionId', '==', institutionId)
        );
        const paymentsSnapshot = await getDocs(paymentsQuery);
        for (const paymentDoc of paymentsSnapshot.docs) {
          await deleteDoc(paymentDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allPaymentsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_PAYMENTS));
          for (const paymentDoc of allPaymentsSnapshot.docs) {
            const paymentData = paymentDoc.data();
            if (paymentData.institutionId === institutionId) {
              await deleteDoc(paymentDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 3. Delete institution customizations
      try {
        const customizationRef = doc(db, COLLECTIONS.ADMISSIONS_CUSTOMIZATIONS, institutionId);
        const customizationDoc = await getDoc(customizationRef);
        if (customizationDoc.exists()) {
          await deleteDoc(customizationRef);
        }
      } catch (error) {
      }

      // 4. Delete all curriculum items for this institution
      try {
        const curriculumQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM),
          where('institutionId', '==', institutionId)
        );
        const curriculumSnapshot = await getDocs(curriculumQuery);
        for (const curriculumDoc of curriculumSnapshot.docs) {
          await deleteDoc(curriculumDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allCurriculumSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_CURRICULUM));
          for (const curriculumDoc of allCurriculumSnapshot.docs) {
            const curriculumData = curriculumDoc.data();
            if (curriculumData.institutionId === institutionId) {
              await deleteDoc(curriculumDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 5. Delete all materials (brochures) for this institution
      try {
        const materialsQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_MATERIALS),
          where('institutionId', '==', institutionId)
        );
        const materialsSnapshot = await getDocs(materialsQuery);
        for (const materialDoc of materialsSnapshot.docs) {
          await deleteDoc(materialDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allMaterialsSnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_MATERIALS));
          for (const materialDoc of allMaterialsSnapshot.docs) {
            const materialData = materialDoc.data();
            if (materialData.institutionId === institutionId) {
              await deleteDoc(materialDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 6. Delete all faculty for this institution
      try {
        const facultyQuery = query(
          collection(db, COLLECTIONS.ADMISSIONS_FACULTY),
          where('institutionId', '==', institutionId)
        );
        const facultySnapshot = await getDocs(facultyQuery);
        for (const facultyDoc of facultySnapshot.docs) {
          await deleteDoc(facultyDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allFacultySnapshot = await getDocs(collection(db, COLLECTIONS.ADMISSIONS_FACULTY));
          for (const facultyDoc of allFacultySnapshot.docs) {
            const facultyData = facultyDoc.data();
            if (facultyData.institutionId === institutionId) {
              await deleteDoc(facultyDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 7. Delete payment refunds related to this institution
      try {
        const refundsQuery = query(
          collection(db, COLLECTIONS.PAYMENT_REFUNDS),
          where('institutionId', '==', institutionId)
        );
        const refundsSnapshot = await getDocs(refundsQuery);
        for (const refundDoc of refundsSnapshot.docs) {
          await deleteDoc(refundDoc.ref);
        }
      } catch (error) {
        // Try fallback
        try {
          const allRefundsSnapshot = await getDocs(collection(db, COLLECTIONS.PAYMENT_REFUNDS));
          for (const refundDoc of allRefundsSnapshot.docs) {
            const refundData = refundDoc.data();
            if (refundData.institutionId === institutionId) {
              await deleteDoc(refundDoc.ref);
            }
          }
        } catch (fallbackError) {
        }
      }

      // 8. Finally, delete the institution profile itself
      try {
        const institutionRef = doc(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS, institutionId);
        const institutionDoc = await getDoc(institutionRef);
        if (institutionDoc.exists()) {
          await deleteDoc(institutionRef);
        }
      } catch (error) {
        throw error; // Re-throw if we can't delete the main profile
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== FEE STRUCTURE MANAGEMENT ==========
  // Get fee structure for institution
  async getInstitutionFeeStructure(institutionId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_FEE_STRUCTURE, institutionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, feeStructure: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: true, feeStructure: null };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save fee structure (one per institution)
  async saveFeeStructure(feeStructureData) {
    try {
      const institutionId = feeStructureData.institutionId;
      if (!institutionId) {
        return { success: false, error: 'Institution ID is required' };
      }

      const data = {
        ...feeStructureData,
        updatedAt: serverTimestamp()
      };

      const docRef = doc(db, COLLECTIONS.ADMISSIONS_FEE_STRUCTURE, institutionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        // Update existing
        await updateDoc(docRef, data);
        return { success: true, feeStructureId: institutionId };
      } else {
        // Create new
        data.createdAt = serverTimestamp();
        await setDoc(docRef, data);
        return { success: true, feeStructureId: institutionId };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Upload fee structure PDF
  async uploadFeeStructurePDF(file, institutionId) {
    try {
      const storagePath = `admissions/fee-structure/${institutionId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, storagePath);
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      return { success: true, url: result.url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete fee structure
  async deleteFeeStructure(institutionId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_FEE_STRUCTURE, institutionId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // Delete PDF from R2 storage if exists
        if (data.pdfUrl) {
          try {
            // Extract file key from URL (assuming URL contains the path)
            const fileKey = data.pdfUrl.split('/mahstry-storage/')[1];
            if (fileKey) {
              await deleteFile(fileKey);
            }
          } catch (storageError) {
            // Continue even if storage deletion fails
            console.warn('Failed to delete PDF from storage:', storageError);
          }
        }
        await deleteDoc(docRef);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== RESULTS MANAGEMENT ==========
  // Get results for institution
  async getInstitutionResults(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_RESULTS),
        where('institutionId', '==', institutionId),
        orderBy('year', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const results = [];
      querySnapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, results };
    } catch (error) {
      // Fallback if index doesn't exist
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_RESULTS),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const results = [];
        querySnapshot.forEach((doc) => {
          results.push({ id: doc.id, ...doc.data() });
        });
        results.sort((a, b) => {
          const yearA = a.year || '';
          const yearB = b.year || '';
          return yearB.localeCompare(yearA);
        });
        return { success: true, results };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Save result
  async saveResult(resultData, resultId = null) {
    try {
      const data = {
        ...resultData,
        updatedAt: serverTimestamp()
      };

      if (resultId) {
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_RESULTS, resultId);
        await updateDoc(docRef, data);
        return { success: true, resultId };
      } else {
        data.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_RESULTS), data);
        return { success: true, resultId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete result
  async deleteResult(resultId) {
    try {
      await deleteDoc(doc(db, COLLECTIONS.ADMISSIONS_RESULTS, resultId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get institution by subdomain
  async getInstitutionBySubdomain(subdomain) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS),
        where('subdomain', '==', subdomain.toLowerCase()),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { success: true, institution: { id: doc.id, ...doc.data() } };
      } else {
        return { success: false, error: 'Institution not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check if subdomain is available (for validation)
  async isSubdomainAvailable(subdomain, excludeInstitutionId = null) {
    try {
      const normalizedSubdomain = subdomain.toLowerCase().trim();
      
      // Check if subdomain is empty or invalid
      if (!normalizedSubdomain || !/^[a-z0-9-]+$/.test(normalizedSubdomain)) {
        return { success: false, available: false, error: 'Invalid subdomain format. Use only lowercase letters, numbers, and hyphens.' };
      }

      // Reserved subdomains that shouldn't be used
      const reserved = ['www', 'admin', 'api', 'app', 'mail', 'ftp', 'localhost', 'dashboard', 'coaching-admin', 'founder', 'teacher', 'student'];
      if (reserved.includes(normalizedSubdomain)) {
        return { success: false, available: false, error: 'This subdomain is reserved and cannot be used.' };
      }

      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_INSTITUTIONS),
        where('subdomain', '==', normalizedSubdomain),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      // If we're editing an existing institution, exclude it from the check
      if (!querySnapshot.empty) {
        const existingDoc = querySnapshot.docs[0];
        if (excludeInstitutionId && existingDoc.id === excludeInstitutionId) {
          return { success: true, available: true };
        }
        return { success: true, available: false, error: 'This subdomain is already taken.' };
      }
      
      return { success: true, available: true };
    } catch (error) {
      return { success: false, available: false, error: error.message };
    }
  },

  // Generate a unique subdomain suggestion based on institution name
  async generateSubdomainSuggestion(institutionName, institutionId = null) {
    try {
      // Convert name to subdomain format
      let baseSubdomain = institutionName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Remove multiple hyphens
        .substring(0, 30); // Limit length

      // Check if base subdomain is available
      const baseCheck = await this.isSubdomainAvailable(baseSubdomain, institutionId);
      if (baseCheck.available) {
        return { success: true, subdomain: baseSubdomain };
      }

      // If not available, try with numbers
      for (let i = 1; i <= 99; i++) {
        const suggestion = `${baseSubdomain}-${i}`;
        const check = await this.isSubdomainAvailable(suggestion, institutionId);
        if (check.available) {
          return { success: true, subdomain: suggestion };
        }
      }

      // If still not found, add random string
      const randomStr = Math.random().toString(36).substring(2, 6);
      const finalSuggestion = `${baseSubdomain}-${randomStr}`;
      return { success: true, subdomain: finalSuggestion };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // ========== ADMISSION TEST MANAGEMENT ==========
  // Get all admission tests for institution
  async getAdmissionTests(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG),
        where('institutionId', '==', institutionId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const tests = [];
      
      querySnapshot.forEach((doc) => {
        tests.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, tests };
    } catch (error) {
      // Fallback without orderBy
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const tests = [];
        
        querySnapshot.forEach((doc) => {
          tests.push({ id: doc.id, ...doc.data() });
        });
        
        tests.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, tests };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Get single admission test by ID
  async getAdmissionTestById(testId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG, testId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { success: true, test: { id: docSnap.id, ...docSnap.data() } };
      } else {
        return { success: false, error: 'Test not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Save admission test (create or update)
  async saveAdmissionTest(institutionId, testData, testId = null) {
    try {
      const data = {
        ...testData,
        institutionId,
        updatedAt: serverTimestamp()
      };

      if (testId) {
        // Update existing test
        const docRef = doc(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG, testId);
        await updateDoc(docRef, data);
        return { success: true, testId };
      } else {
        // Create new test
        data.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG), data);
        return { success: true, testId: docRef.id };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete admission test
  async deleteAdmissionTest(testId) {
    try {
      // Delete all registrations for this test
      const registrationsQuery = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
        where('testId', '==', testId)
      );
      const registrationsSnapshot = await getDocs(registrationsQuery);
      for (const regDoc of registrationsSnapshot.docs) {
        await deleteDoc(regDoc.ref);
      }

      // Delete the test itself
      await deleteDoc(doc(db, COLLECTIONS.ADMISSIONS_TEST_CONFIG, testId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Legacy function for backward compatibility - gets first test
  async getAdmissionTestConfig(institutionId) {
    const result = await this.getAdmissionTests(institutionId);
    if (result.success && result.tests.length > 0) {
      return { success: true, testConfig: result.tests[0] };
    }
    return { success: true, testConfig: null };
  },

  // Legacy function for backward compatibility
  async saveAdmissionTestConfig(institutionId, testData) {
    return await this.saveAdmissionTest(institutionId, testData, testData.id || null);
  },

  // Register user for admission test
  async registerForAdmissionTest(testId, institutionId, userId, userData) {
    try {
      const data = {
        testId,
        institutionId,
        userId,
        studentName: userData.studentName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        hasApplied: false,
        admitCardUrl: null,
        registeredAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS), data);
      return { success: true, registrationId: docRef.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check if user is registered for specific admission test
  async isUserRegisteredForTest(testId, userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
        where('testId', '==', testId),
        where('userId', '==', userId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const registration = querySnapshot.docs[0];
        return { 
          success: true, 
          isRegistered: true,
          registration: { id: registration.id, ...registration.data() }
        };
      }
      
      return { success: true, isRegistered: false, registration: null };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get test registrations for specific test (admin)
  async getTestRegistrations(testId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
        where('testId', '==', testId),
        orderBy('registeredAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const registrations = [];
      
      querySnapshot.forEach((doc) => {
        registrations.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, registrations };
    } catch (error) {
      // Fallback without orderBy
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
          where('testId', '==', testId)
        );
        const querySnapshot = await getDocs(q);
        const registrations = [];
        
        querySnapshot.forEach((doc) => {
          registrations.push({ id: doc.id, ...doc.data() });
        });
        
        registrations.sort((a, b) => {
          const aTime = a.registeredAt?.toDate?.() || new Date(0);
          const bTime = b.registeredAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, registrations };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Get all test registrations for institution (all tests)
  async getAllTestRegistrationsForInstitution(institutionId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
        where('institutionId', '==', institutionId),
        orderBy('registeredAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const registrations = [];
      
      querySnapshot.forEach((doc) => {
        registrations.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, registrations };
    } catch (error) {
      // Fallback without orderBy
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
          where('institutionId', '==', institutionId)
        );
        const querySnapshot = await getDocs(q);
        const registrations = [];
        
        querySnapshot.forEach((doc) => {
          registrations.push({ id: doc.id, ...doc.data() });
        });
        
        registrations.sort((a, b) => {
          const aTime = a.registeredAt?.toDate?.() || new Date(0);
          const bTime = b.registeredAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, registrations };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Upload admit card for a registration
  async uploadAdmitCard(testId, registrationId, file) {
    try {
      const storagePath = `admissions/admit-cards/${testId}/${registrationId}_${Date.now()}.pdf`;
      const result = await uploadFile(file, storagePath);
      
      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }
      
      const downloadURL = result.url;

      // Update registration with admit card URL
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS, registrationId);
      await updateDoc(docRef, {
        admitCardUrl: downloadURL,
        admitCardUploadedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true, url: downloadURL };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Mark test registration as applied (when user completes full application)
  async markTestRegistrationAsApplied(registrationId) {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS, registrationId);
      await updateDoc(docRef, {
        hasApplied: true,
        appliedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get user's test registrations
  async getUserTestRegistrations(userId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
        where('userId', '==', userId),
        orderBy('registeredAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const registrations = [];
      
      querySnapshot.forEach((doc) => {
        registrations.push({ id: doc.id, ...doc.data() });
      });
      
      return { success: true, registrations };
    } catch (error) {
      // Fallback without orderBy
      try {
        const q = query(
          collection(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS),
          where('userId', '==', userId)
        );
        const querySnapshot = await getDocs(q);
        const registrations = [];
        
        querySnapshot.forEach((doc) => {
          registrations.push({ id: doc.id, ...doc.data() });
        });
        
        registrations.sort((a, b) => {
          const aTime = a.registeredAt?.toDate?.() || new Date(0);
          const bTime = b.registeredAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        
        return { success: true, registrations };
      } catch (fallbackError) {
        return { success: false, error: fallbackError.message };
      }
    }
  },

  // Update test result status (Selected/Rejected)
  async updateTestResult(registrationId, resultStatus, remarks = '') {
    try {
      const docRef = doc(db, COLLECTIONS.ADMISSIONS_TEST_REGISTRATIONS, registrationId);
      await updateDoc(docRef, {
        testResult: resultStatus, // 'selected' or 'rejected'
        testResultRemarks: remarks,
        testResultUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};

